import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Calculator, ChevronDown, Compass, FolderOpen, HelpCircle, LogOut, MessageSquare, MessageSquarePlus, Settings, User,
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useChatPaneStore } from '../../store/chatPaneStore';
import { useTourStore } from '../../tours/tourStore';
import { UrawaIcon } from '../UrawaIcon';
import { WHATSAPP_URL, WhatsAppIcon } from '../../pages/landing/data';

const MENU_ROW_CLS =
  'w-full flex items-center gap-3 px-4 py-2.5 text-sm text-legal-700 hover:bg-legal-50 transition-colors text-left';

export function Navbar() {
  const { user, logout } = useAuthStore();
  const requestTour = useTourStore((s) => s.request);
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const chatOpen = useChatPaneStore((s) => s.isOpen);
  const toggleChat = useChatPaneStore((s) => s.toggle);
  // On /app/research/new the page IS the chat, so the pane is hidden there.
  // Decision: the button doesn't navigate or toggle — it focuses the page's
  // own composer (consistent: the button always means "put me in the chat").
  const onIntakePage = pathname === '/app/research/new';

  const close = () => setMenuOpen(false);

  return (
    <nav className="bg-legal-900 border-b border-legal-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 gap-2">
          <div className="flex items-center min-w-0">
            <UrawaIcon className="h-7 w-7 text-accent flex-shrink-0" />
            <span className="ml-1.5 text-xl font-bold text-white">Urava</span>
            <span className="hidden sm:inline text-xs text-legal-400 border border-legal-700 rounded px-1.5 py-0.5 ml-1">
              Legal AI
            </span>
            <span className="text-xs font-semibold text-amber-400 border border-amber-500/40 bg-amber-500/10 rounded px-1.5 py-0.5 ml-1 flex-shrink-0">
              Beta
            </span>
          </div>

          {user && (
            <div className="flex items-center gap-2 min-w-0">
              {/* Global research chat toggle — desktop only (mobile has the FAB).
                  Labeled, never a bare icon (NN/g: users can't infer chatbot
                  purpose from a sparkle). */}
              <button
                type="button"
                data-tour="research-chat-button"
                data-track="nav-research-chat"
                aria-pressed={onIntakePage ? undefined : chatOpen}
                onClick={() => {
                  if (onIntakePage) {
                    document.querySelector<HTMLTextAreaElement>('main textarea')?.focus();
                  } else {
                    toggleChat();
                  }
                }}
                className="hidden lg:inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-legal-700 text-legal-200 hover:text-white hover:border-legal-500 transition-colors text-sm press"
              >
                <MessageSquarePlus className="h-4 w-4 text-accent" />
                <span>Ask a legal question</span>
              </button>

              <div className="relative min-w-0">
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                data-tour="user-menu"
                data-track="nav-user-menu"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                className="flex items-center space-x-2 text-legal-300 hover:text-white min-w-0 px-2 py-1.5 rounded-lg transition-colors press"
              >
                <User className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm truncate max-w-[40vw] sm:max-w-none">
                  <span className="sm:hidden">{user.full_name.split(' ')[0]}</span>
                  <span className="hidden sm:inline">{user.full_name}</span>
                </span>
                <ChevronDown
                  className={`h-4 w-4 flex-shrink-0 transition-transform ${menuOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {menuOpen && (
                <>
                  <button
                    type="button"
                    aria-label="Close menu"
                    onClick={close}
                    className="fixed inset-0 z-40 cursor-default"
                  />
                  <div
                    role="menu"
                    className="absolute right-0 top-full mt-2 z-50 w-64 bg-white rounded-card border border-legal-200 shadow-elevation-3 py-1.5 animate-slide-up"
                  >
                    <Link to="/app/settings" onClick={close} data-track="usermenu-settings" className={MENU_ROW_CLS}>
                      <Settings className="h-4 w-4 text-legal-400" />
                      <span>Settings</span>
                    </Link>
                    {/* Demoted from the sidebar (IA, July 2026) — the library
                        stays one click away on desktop. */}
                    <Link to="/app/documents" onClick={close} data-track="usermenu-documents" className={MENU_ROW_CLS}>
                      <FolderOpen className="h-4 w-4 text-legal-400" />
                      <span>All documents</span>
                    </Link>
                    {/* Permanent pull entry point for the orientation tour (tour spec §2.2) */}
                    <button
                      type="button"
                      onClick={() => {
                        close();
                        requestTour('around', '/app');
                      }}
                      data-track="usermenu-tour"
                      className={MENU_ROW_CLS}
                    >
                      <Compass className="h-4 w-4 text-legal-400" />
                      <span>Show me around</span>
                    </button>
                    <Link to="/user-guide" onClick={close} data-track="usermenu-guide" className={MENU_ROW_CLS}>
                      <HelpCircle className="h-4 w-4 text-legal-400" />
                      <span>How Urava works</span>
                    </Link>
                    <Link to="/feedback" onClick={close} data-track="usermenu-feedback" className={MENU_ROW_CLS}>
                      <MessageSquare className="h-4 w-4 text-legal-400" />
                      <span>Send feedback</span>
                    </Link>
                    <a
                      href={WHATSAPP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={close}
                      data-track="usermenu-whatsapp"
                      className={MENU_ROW_CLS}
                    >
                      <WhatsAppIcon className="h-4 w-4 text-[#25D366]" />
                      <span>Urava on WhatsApp</span>
                    </a>
                    {/* Edge-worker page — full page load in a new tab, not a SPA <Link> */}
                    <a
                      href="/tools"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={close}
                      data-track="usermenu-tools"
                      className={MENU_ROW_CLS}
                    >
                      <Calculator className="h-4 w-4 text-legal-400" />
                      <span>Free legal calculators</span>
                    </a>
                    <div className="my-1.5 border-t border-legal-100" />
                    <button
                      type="button"
                      onClick={() => {
                        close();
                        logout();
                      }}
                      data-track="usermenu-logout"
                      className={MENU_ROW_CLS}
                    >
                      <LogOut className="h-4 w-4 text-legal-400" />
                      <span>Log out</span>
                    </button>
                  </div>
                </>
              )}
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
